import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lightbulb, X, Star, Sparkles } from "lucide-react";

const CATS = [
  { id: "New feature 🆕", label: "New feature 🆕" },
  { id: "Improvement ✨", label: "Improvement ✨" },
  { id: "Report format 📄", label: "Report format 📄" },
  { id: "Bug / error 🐞", label: "Bug / error 🐞" },
  { id: "Odia translation 🗣️", label: "Odia translation 🗣️" },
  { id: "Other 💬", label: "Other 💬" },
];

export function SuggestionForm({ onDone }) {
  const { user } = useAuth();
  const [category, setCategory] = useState("New feature 🆕");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [anonymous, setAnonymous] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [mobile, setMobile] = useState(user?.mobile || "");
  const [screenshot, setScreenshot] = useState("");
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Image must be under 2 MB."); return; }
    const r = new FileReader();
    r.onload = () => setScreenshot(r.result);
    r.readAsDataURL(f);
  };

  const submit = async () => {
    if (message.trim().length < 10) { toast.error("Please write at least 10 characters."); return; }
    setBusy(true);
    try {
      await api.post("/public/suggestions", { category, message, rating, anonymous, name, mobile, screenshot, company });
      toast.success("Thank you! Your suggestion helps teachers across Odisha.");
      setMessage(""); setRating(0); setScreenshot("");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-semibold">Category</label>
        <div className="flex flex-wrap gap-2 mt-2">
          {CATS.map((c) => (
            <button key={c.id} onClick={() => setCategory(c.id)} data-testid={`sugg-cat-${c.id}`}
              className={`px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${category === c.id ? "bg-thali-green text-white border-thali-green" : "bg-white border-thali-turmeric/40 text-thali-ink/70"}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-sm font-semibold flex justify-between"><span>Your suggestion *</span><span className="text-thali-ink/40 font-normal">{message.length}/1000</span></label>
        <textarea maxLength={1000} className="input-thali mt-1 min-h-[110px]" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us what would help you…" data-testid="sugg-message" />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold">Rate the app</span>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setRating(n)} aria-label={`${n} star`} data-testid={`sugg-star-${n}`}>
            <Star className={`w-6 h-6 ${n <= rating ? "fill-thali-turmeric text-thali-turmericdark" : "text-thali-ink/30"}`} />
          </button>
        ))}
      </div>
      {!anonymous && (
        <div className="grid grid-cols-2 gap-2">
          <input className="input-thali h-11" placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="sugg-name" />
          <input className="input-thali h-11" placeholder="Mobile (optional)" inputMode="numeric" value={mobile} onChange={(e) => setMobile(e.target.value)} data-testid="sugg-mobile" />
        </div>
      )}
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="w-4 h-4 accent-thali-green" data-testid="sugg-anon" /> Send anonymously
      </label>
      <div>
        <label className="text-sm font-semibold">Screenshot (optional, max 2 MB)</label>
        <input type="file" accept="image/png,image/jpeg" onChange={onFile} className="mt-1 text-sm" data-testid="sugg-file" />
        {screenshot && <img src={screenshot} alt="preview" className="mt-2 h-20 rounded-lg border border-thali-turmeric/30" />}
      </div>
      <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <button onClick={submit} disabled={busy} className="btn-3d-green w-full" data-testid="sugg-submit"><Sparkles className="w-4 h-4" /> {busy ? "Sending…" : "Send Suggestion"}</button>
    </div>
  );
}

export default function SuggestionButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="suggestion-fab"
        className="fixed z-40 right-4 bottom-[76px] md:bottom-6 bg-thali-saffron text-white rounded-full shadow-btn-saffron min-h-[52px] px-4 font-heading font-bold inline-flex items-center gap-2 active:translate-y-1 active:shadow-none transition-all">
        <Lightbulb className="w-5 h-5" /> <span className="hidden sm:inline">Suggestion</span>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)}>
          <div className="bg-thali-paper rounded-t-card sm:rounded-card w-full max-w-md max-h-[90vh] overflow-y-auto p-5 animate-fade-in-up" onClick={(e) => e.stopPropagation()} data-testid="suggestion-modal">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-extrabold text-lg text-thali-green flex items-center gap-2"><Lightbulb className="w-5 h-5" /> Suggestion Box</h2>
              <button onClick={() => setOpen(false)} className="w-10 h-10 grid place-items-center" aria-label="Close"><X /></button>
            </div>
            <SuggestionForm onDone={() => setOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
}
